import type { DeviceMetric } from '../../../../services/deviceMetricTypes';
import { metricCategories, MetricCategory } from '../../collection/metrics/categories';
import { humanize } from '../../collection/metrics/formatting';
import type { FieldPresentation, MeasurementContext, MetricInput, MetricPresentation } from '../../collection/metrics/types';

// Labels and categories for route keys shown without a loaded catalog, such as
// export history. Descriptions and verification come only from the API catalog.
type Definition = { label: string; category: MetricCategory };
export const ultrahumanCategories = {
  activity: metricCategories.activity, heart: metricCategories.heart, sleep: metricCategories.sleep,
  body: metricCategories.body, glucose: metricCategories.glucose, other: metricCategories.other,
};
const definitions: Record<string, Definition> = {
  hr: { label: 'Heart rate', category: 'heart' },
  hrv: { label: 'Heart rate variability', category: 'heart' },
  spo2: { label: 'Blood oxygen', category: 'heart' },
  temp: { label: 'Skin temperature', category: 'body' },
  steps: { label: 'Steps', category: 'activity' },
  motion: { label: 'Movement intensity', category: 'activity' },
  night_rhr: { label: 'Resting heart rate', category: 'heart' },
  sleep_rhr: { label: 'Sleep resting heart rate', category: 'sleep' },
  avg_sleep_hrv: { label: 'Average sleep HRV', category: 'sleep' },
  sleep: { label: 'Sleep and sleep stages', category: 'sleep' },
  recovery: { label: 'Recovery', category: 'activity' },
  recovery_index: { label: 'Recovery index', category: 'activity' },
  movement_index: { label: 'Movement index', category: 'activity' },
  active_minutes: { label: 'Active minutes', category: 'activity' },
  inactive_time: { label: 'Inactive time', category: 'activity' },
  weekly_active_minutes: { label: 'Weekly active minutes (reported total)', category: 'activity' },
  movements: { label: 'Movements', category: 'activity' },
  morning_alertness: { label: 'Morning alertness', category: 'sleep' },
  vo2_max: { label: 'VO2 max', category: 'activity' },
  glucose: { label: 'Glucose', category: 'glucose' },
  metabolic_score: { label: 'Metabolic score', category: 'glucose' },
  glucose_variability: { label: 'Glucose variability', category: 'glucose' },
  average_glucose: { label: 'Average glucose', category: 'glucose' },
  hba1c: { label: 'Estimated HbA1c', category: 'glucose' },
  time_in_target: { label: 'Time in glucose target range', category: 'glucose' },
  other: { label: 'Other Ultrahuman measurements', category: 'other' },
};

export function ultrahumanMetricKey(metric: MetricInput): string {
  return (metric.routeKey?.startsWith('ultrahuman_') ? metric.routeKey : metric.dataType).replace(/^ultrahuman_/, '');
}

export function metricPresentation(metric: MetricInput): MetricPresentation {
  const key = ultrahumanMetricKey(metric);
  const summary = key.endsWith('_summary');
  const definition = definitions[key.replace(/_summary$/, '')];
  const declared = (Object.keys(ultrahumanCategories) as Array<keyof typeof ultrahumanCategories>).find(category => ultrahumanCategories[category].label === metric.category);
  const category = declared || definition?.category || 'other';
  const palette = metricCategories[category];
  const label = definition?.label || humanize(key);
  return { category, ...palette,
    label: metric.label || (summary ? `${label} (daily summary)` : label),
    description: metric.description || (summary ? 'The daily summary reported by Ultrahuman, separate from individual readings.' :
      'Measurements reported by Ultrahuman.'),
    // Verification comes from Ultrahuman's API catalog, never a similarly named Fitbit metric.
    verified: metric.verification === 'verified', unverified: metric.verification !== 'verified',
  };
}

export function recordingKind(metric: DeviceMetric) {
  if (ultrahumanMetricKey(metric) === 'weekly_active_minutes') return 'Weekly total snapshots';
  if (metric.temporalKind === 'daily' || metric.operation === 'daily_rollup') return 'Daily summaries';
  if (ultrahumanMetricKey(metric) === 'sleep') return 'Sleep sessions';
  return metric.temporalKind === 'point' ? 'Individual readings' : 'Records';
}

export function fieldPresentation(metric: DeviceMetric, field: DeviceMetric['fields'][number]): FieldPresentation {
  const labels: Record<string, string> = { '$.value': 'Value', '$.avg': 'Average', '$.total': 'Total',
    '$.last_reading': 'Last reading', '$.score': 'Sleep score', '$.totalSleepSeconds': 'Time asleep', '$.sleepEfficiency': 'Sleep efficiency' };
  const unit = ['C', 'Cel', '°C'].includes(field.unit || '') ? '°C' : field.unit || '';
  const additive = ultrahumanMetricKey(metric) === 'steps' && field.path === '$.value';
  // A verified metric names the fields its review covered; the others stay flagged.
  const reviewed = metric.verification === 'verified' ? metric.verificationDetails?.fields : undefined;
  return { label: labels[field.path] || humanize(field.path), unit, divisor: 1, additive, defaultAggregation: additive ? 'total' : 'average',
    unreviewed: Boolean(reviewed && !reviewed.includes(field.path)) };
}

export const defaultField = (metric: DeviceMetric | undefined) => metric?.fields[0]?.path || '';
export const recordKindLabel = () => 'Stored record';

const pointTypes = new Set(['hr', 'hrv', 'spo2', 'temp', 'steps', 'motion', 'night_rhr', 'glucose']);
export function measurementContext(metric: DeviceMetric, _fieldPath: string): MeasurementContext | undefined {
  const key = ultrahumanMetricKey(metric);
  const base = key.replace(/_summary$/, '');
  if (pointTypes.has(base)) return {
    title: `${definitions[base].label}: readings and daily summaries`,
    description: 'Individual readings retain their timestamps. Daily summaries contain the provider\'s reported values for a date. ' +
      'Compare the views separately; adding a summary to its readings can count the same measurement twice.' +
      (base === 'hrv' ? ' Ultrahuman does not specify an HRV calculation method in the reviewed records, so these values are not labeled RMSSD.' : ''),
    related: [{ dataType: key.endsWith('_summary') ? base : `${base}_summary`, fieldPath: '$.value' }],
  };
  if (key === 'weekly_active_minutes') return {
    title: 'Weekly activity snapshots',
    description: 'Each date stores the reported weekly total. These overlapping totals must not be added across dates or combined with daily active minutes.',
    related: [],
  };
  if (key === 'sleep') return {
    title: 'Ultrahuman sleep records',
    description: 'Sleep scores, recorded sleep periods and sleep stages describe different aspects of sleep. An empty sleep object is not a recorded session.',
    related: [],
  };
  return undefined;
}
